import { useState, useMemo } from "react";
import {
  Layers,
  ChevronDown,
  ChevronUp,
  X,
  Plus,
  Shield,
  Zap,
  Gauge,
  SlidersHorizontal,
  Info,
  ArrowUpDown,
  Lock,
  Activity,
  DollarSign,
  Clock,
} from "lucide-react";
import type { ProviderCatalogItem } from "../../gen/models/providerCatalogItem";

export interface ProviderPreferencesState {
  order?: string[];
  allow_fallbacks?: boolean;
  require_parameters?: boolean;
  data_collection?: "allow" | "deny";
  zdr?: boolean;
  enforce_distillable_text?: boolean;
  only?: string[];
  ignore?: string[];
  quantizations?: string[];
  sort?: string;
  preferred_min_throughput?: number | null;
  preferred_max_latency?: number | null;
  max_price?: {
    prompt?: number;
    completion?: number;
  };
}

interface ProviderSelectorProps {
  modelId: string;
  preferences: ProviderPreferencesState;
  onChange: (next: ProviderPreferencesState) => void;
  availableProviders: ProviderCatalogItem[];
  isLoading?: boolean;
  isOpenRouterModel?: boolean;
}

type ProviderSortField = "priority" | "name" | "latency" | "throughput" | "uptime" | "price";
type SortDirection = "asc" | "desc";

export function ProviderSelector({
  modelId,
  preferences,
  onChange,
  availableProviders,
  isLoading = false,
  isOpenRouterModel = true,
}: ProviderSelectorProps) {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [providerSearch, setProviderSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [sortField, setSortField] = useState<ProviderSortField>("priority");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  const selectedOrder = preferences.order || [];

  const handleSort = (field: ProviderSortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      // For throughput and uptime, natural default is descending (higher is better)
      if (field === "throughput" || field === "uptime") {
        setSortDir("desc");
      } else {
        setSortDir("asc");
      }
    }
  };

  const sortedProviders = useMemo(() => {
    const list = [...availableProviders];
    list.sort((a, b) => {
      let comparison = 0;
      if (sortField === "priority") {
        const aIdx = selectedOrder.indexOf(a.slug);
        const bIdx = selectedOrder.indexOf(b.slug);
        const aSelected = aIdx !== -1;
        const bSelected = bIdx !== -1;
        if (aSelected && !bSelected) return sortDir === "asc" ? -1 : 1;
        if (!aSelected && bSelected) return sortDir === "asc" ? 1 : -1;
        if (aSelected && bSelected) {
          comparison = aIdx - bIdx;
        } else {
          comparison = a.name.localeCompare(b.name);
        }
      } else if (sortField === "name") {
        comparison = a.name.localeCompare(b.name);
      } else if (sortField === "latency") {
        const aVal = a.latency_p90_ms ?? Infinity;
        const bVal = b.latency_p90_ms ?? Infinity;
        comparison = aVal - bVal;
      } else if (sortField === "throughput") {
        const aVal = a.throughput_p50_tps ?? -1;
        const bVal = b.throughput_p50_tps ?? -1;
        comparison = aVal - bVal;
      } else if (sortField === "uptime") {
        const aVal = a.uptime_pct ?? -1;
        const bVal = b.uptime_pct ?? -1;
        comparison = aVal - bVal;
      } else if (sortField === "price") {
        const aVal = a.prompt_price_per_mtok ?? Infinity;
        const bVal = b.prompt_price_per_mtok ?? Infinity;
        comparison = aVal - bVal;
      }
      return sortDir === "asc" ? comparison : -comparison;
    });
    return list;
  }, [availableProviders, selectedOrder, sortField, sortDir]);

  const filteredProviders = useMemo(() => {
    const q = providerSearch.toLowerCase().trim();
    return availableProviders.filter(
      (p) =>
        !selectedOrder.includes(p.slug) &&
        (p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q))
    );
  }, [availableProviders, selectedOrder, providerSearch]);

  const handleAddProvider = (slug: string) => {
    const nextOrder = [...selectedOrder, slug];
    onChange({
      ...preferences,
      order: nextOrder,
    });
    setProviderSearch("");
    setIsDropdownOpen(false);
  };

  const handleRemoveProvider = (slug: string) => {
    const nextOrder = selectedOrder.filter((s) => s !== slug);
    onChange({
      ...preferences,
      order: nextOrder.length > 0 ? nextOrder : undefined,
    });
  };

  const handleMoveProvider = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= selectedOrder.length) return;
    const nextOrder = [...selectedOrder];
    const temp = nextOrder[index];
    nextOrder[index] = nextOrder[target];
    nextOrder[target] = temp;
    onChange({
      ...preferences,
      order: nextOrder,
    });
  };

  const providerMap = useMemo(() => {
    const m = new Map<string, ProviderCatalogItem>();
    for (const p of availableProviders) {
      m.set(p.slug, p);
    }
    return m;
  }, [availableProviders]);

  return (
    <div className="space-y-4 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40 p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="space-y-0.5">
          <label className="text-xs font-bold text-stone-900 dark:text-stone-100 flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-stone-600 dark:text-stone-400" />
            <span>OpenRouter Provider Selection & Routing</span>
          </label>
          <p className="text-[11px] text-stone-500 dark:text-stone-400">
            Available hosting providers and live performance parameters for <strong className="font-mono text-stone-700 dark:text-stone-300">{modelId}</strong>.
          </p>
        </div>

        {!isOpenRouterModel && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 self-start sm:self-auto">
            <Info className="w-3 h-3" />
            Active on OpenRouter models
          </span>
        )}
      </div>

      {/* Available Providers Table for Model */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-stone-700 dark:text-stone-300 flex items-center gap-1">
            <span>Available Providers for this Model</span>
            {isLoading && <span className="text-[10px] text-stone-400">(updating live metrics...)</span>}
          </span>
          <span className="text-[10px] text-stone-400 font-mono">
            {availableProviders.length} endpoint{availableProviders.length !== 1 ? "s" : ""}
          </span>
        </div>

        {availableProviders.length === 0 ? (
          <div className="p-3 rounded-lg border border-dashed border-stone-200 dark:border-stone-800 text-[11px] text-stone-400 text-center">
            No specific provider endpoints found for this model. Default OpenRouter routing applies.
          </div>
        ) : (
          <div className="border border-stone-200 dark:border-stone-800 rounded-lg overflow-hidden bg-white dark:bg-stone-900 shadow-2xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-800/50 text-[11px] text-stone-600 dark:text-stone-400">
                    <th
                      scope="col"
                      onClick={() => handleSort("priority")}
                      className="py-2 px-3 font-semibold cursor-pointer select-none hover:text-stone-900 dark:hover:text-stone-200"
                    >
                      <div className="flex items-center gap-1">
                        <span>Priority / State</span>
                        <ArrowUpDown className={`w-3 h-3 ${sortField === "priority" ? "text-stone-900 dark:text-stone-100" : "text-stone-400"}`} />
                      </div>
                    </th>
                    <th
                      scope="col"
                      onClick={() => handleSort("name")}
                      className="py-2 px-3 font-semibold cursor-pointer select-none hover:text-stone-900 dark:hover:text-stone-200"
                    >
                      <div className="flex items-center gap-1">
                        <span>Provider</span>
                        <ArrowUpDown className={`w-3 h-3 ${sortField === "name" ? "text-stone-900 dark:text-stone-100" : "text-stone-400"}`} />
                      </div>
                    </th>
                    <th
                      scope="col"
                      onClick={() => handleSort("latency")}
                      className="py-2 px-3 font-semibold cursor-pointer select-none hover:text-stone-900 dark:hover:text-stone-200"
                    >
                      <div className="flex items-center gap-1">
                        <span>Latency (p90)</span>
                        <ArrowUpDown className={`w-3 h-3 ${sortField === "latency" ? "text-stone-900 dark:text-stone-100" : "text-stone-400"}`} />
                      </div>
                    </th>
                    <th
                      scope="col"
                      onClick={() => handleSort("throughput")}
                      className="py-2 px-3 font-semibold cursor-pointer select-none hover:text-stone-900 dark:hover:text-stone-200"
                    >
                      <div className="flex items-center gap-1">
                        <span>Throughput</span>
                        <ArrowUpDown className={`w-3 h-3 ${sortField === "throughput" ? "text-stone-900 dark:text-stone-100" : "text-stone-400"}`} />
                      </div>
                    </th>
                    <th
                      scope="col"
                      onClick={() => handleSort("uptime")}
                      className="py-2 px-3 font-semibold cursor-pointer select-none hover:text-stone-900 dark:hover:text-stone-200"
                    >
                      <div className="flex items-center gap-1">
                        <span>Uptime</span>
                        <ArrowUpDown className={`w-3 h-3 ${sortField === "uptime" ? "text-stone-900 dark:text-stone-100" : "text-stone-400"}`} />
                      </div>
                    </th>
                    <th
                      scope="col"
                      onClick={() => handleSort("price")}
                      className="py-2 px-3 font-semibold cursor-pointer select-none hover:text-stone-900 dark:hover:text-stone-200"
                    >
                      <div className="flex items-center gap-1">
                        <span>Price (Prompt / Comp)</span>
                        <ArrowUpDown className={`w-3 h-3 ${sortField === "price" ? "text-stone-900 dark:text-stone-100" : "text-stone-400"}`} />
                      </div>
                    </th>
                    <th scope="col" className="py-2 px-3 font-semibold text-right">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 dark:divide-stone-800/80">
                  {sortedProviders.map((p) => {
                    const isSelected = selectedOrder.includes(p.slug);
                    const orderIndex = selectedOrder.indexOf(p.slug);

                    return (
                      <tr
                        key={p.slug}
                        onClick={() => {
                          if (isSelected) {
                            handleRemoveProvider(p.slug);
                          } else {
                            handleAddProvider(p.slug);
                          }
                        }}
                        className={`cursor-pointer transition-colors ${
                          isSelected
                            ? "bg-stone-50/90 dark:bg-stone-800/50 hover:bg-stone-100/90 dark:hover:bg-stone-800/80"
                            : "hover:bg-stone-50 dark:hover:bg-stone-800/40"
                        }`}
                      >
                        <td className="py-2.5 px-3">
                          {isSelected ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900">
                              #{orderIndex + 1}
                            </span>
                          ) : (
                            <span className="text-[11px] text-stone-400 font-mono">—</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-stone-900 dark:text-stone-100">{p.name}</span>
                            {p.quantization && (
                              <span className="px-1 py-0.2 rounded text-[9px] font-mono bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 border border-stone-200 dark:border-stone-700">
                                {p.quantization}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] font-mono text-stone-400">{p.slug}</div>
                        </td>
                        <td className="py-2.5 px-3">
                          {p.latency_p90_ms != null ? (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200/60 dark:border-amber-900/40"
                              title="90th percentile Time to First Token (TTFT)"
                            >
                              <Clock className="w-2.5 h-2.5" />
                              {Math.round(p.latency_p90_ms)}ms
                            </span>
                          ) : (
                            <span className="text-stone-400 text-[11px] font-mono">—</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          {p.throughput_p50_tps != null ? (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-900/40"
                              title="Median throughput (tokens/second)"
                            >
                              <Gauge className="w-2.5 h-2.5" />
                              {Math.round(p.throughput_p50_tps)} t/s
                            </span>
                          ) : (
                            <span className="text-stone-400 text-[11px] font-mono">—</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          {p.uptime_pct != null ? (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-400 border border-sky-200/60 dark:border-sky-900/40"
                              title="Availability / Uptime"
                            >
                              <Activity className="w-2.5 h-2.5" />
                              {p.uptime_pct.toFixed(1)}%
                            </span>
                          ) : (
                            <span className="text-stone-400 text-[11px] font-mono">—</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          {p.prompt_price_per_mtok != null ? (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"
                              title="Prompt / Completion price per 1M tokens"
                            >
                              <DollarSign className="w-2.5 h-2.5 text-stone-400" />
                              ${p.prompt_price_per_mtok.toFixed(2)} / ${p.completion_price_per_mtok?.toFixed(2) || "0.00"}
                            </span>
                          ) : (
                            <span className="text-stone-400 text-[11px] font-mono">—</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isSelected) {
                                handleRemoveProvider(p.slug);
                              } else {
                                handleAddProvider(p.slug);
                              }
                            }}
                            className={`px-2 py-1 rounded text-[11px] font-medium transition cursor-pointer ${
                              isSelected
                                ? "bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/60 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-900"
                                : "bg-stone-100 hover:bg-stone-200 dark:bg-stone-800 dark:hover:bg-stone-700 text-stone-800 dark:text-stone-200 border border-stone-200 dark:border-stone-700"
                            }`}
                          >
                            {isSelected ? "Remove" : "+ Prioritize"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Provider Multi-Select / Waterfall Order */}
      <div className="space-y-2 pt-2 border-t border-stone-200/60 dark:border-stone-800/60">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-stone-700 dark:text-stone-300">
            Configured Provider Priority Order (Waterfall)
          </span>
          <span className="text-[10px] text-stone-400 font-mono">
            {selectedOrder.length === 0
              ? "Zero providers selected (OpenRouter balances automatically)"
              : `${selectedOrder.length} prioritized provider${selectedOrder.length > 1 ? "s" : ""}`}
          </span>
        </div>

        {/* Selected Provider Badges */}
        {selectedOrder.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 p-2 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 min-h-[40px]">
            {selectedOrder.map((slug, idx) => {
              const item = providerMap.get(slug);
              return (
                <div
                  key={slug}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-stone-100 dark:bg-stone-800 text-stone-800 dark:text-stone-200 border border-stone-200 dark:border-stone-700 group"
                >
                  <span className="text-[10px] font-mono text-stone-400 dark:text-stone-500 font-bold">
                    #{idx + 1}
                  </span>
                  <span>{item ? item.name : slug}</span>
                  {item?.latency_p90_ms != null && (
                    <span className="text-[9px] font-mono text-amber-600 dark:text-amber-400" title="p90 Latency">
                      p90: {Math.round(item.latency_p90_ms)}ms
                    </span>
                  )}
                  {item?.uptime_pct != null && (
                    <span className="text-[9px] font-mono text-sky-600 dark:text-sky-400">
                      {item.uptime_pct.toFixed(0)}%
                    </span>
                  )}
                  <div className="flex items-center gap-0.5 ml-1 border-l border-stone-200 dark:border-stone-700 pl-1">
                    {idx > 0 && (
                      <button
                        type="button"
                        onClick={() => handleMoveProvider(idx, -1)}
                        title="Move higher priority"
                        aria-label="Move provider higher priority"
                        className="p-0.5 hover:text-stone-900 dark:hover:text-stone-100 text-stone-400"
                      >
                        <ChevronDown className="w-3 h-3 rotate-180" />
                      </button>
                    )}
                    {idx < selectedOrder.length - 1 && (
                      <button
                        type="button"
                        onClick={() => handleMoveProvider(idx, 1)}
                        title="Move lower priority"
                        aria-label="Move provider lower priority"
                        className="p-0.5 hover:text-stone-900 dark:hover:text-stone-100 text-stone-400"
                      >
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveProvider(slug)}
                      title="Remove provider"
                      aria-label="Remove provider"
                      className="p-0.5 hover:text-rose-600 dark:hover:text-rose-400 text-stone-400 ml-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add Provider Selector */}
        <div className="relative">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={providerSearch}
                onFocus={() => setIsDropdownOpen(true)}
                onChange={(e) => {
                  setProviderSearch(e.target.value);
                  setIsDropdownOpen(true);
                }}
                placeholder={
                  isLoading
                    ? "Loading available providers..."
                    : selectedOrder.length === 0
                    ? "Add a provider to prioritize (e.g. Anthropic, OpenAI, DeepInfra)..."
                    : "Add another provider to priority fallback chain..."
                }
                className="w-full px-3 py-1.5 text-xs bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:ring-1 focus:ring-stone-400 shadow-2xs"
              />
              {providerSearch && (
                <button
                  type="button"
                  onClick={() => setProviderSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="px-2.5 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-300 text-xs hover:bg-stone-50 dark:hover:bg-stone-800 flex items-center gap-1 shadow-2xs"
            >
              <span>Browse All</span>
              <ChevronDown className={`w-3.5 h-3.5 transition ${isDropdownOpen ? "rotate-180" : ""}`} />
            </button>
          </div>

          {/* Autocomplete / Dropdown Menu */}
          {isDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-20"
                onClick={() => setIsDropdownOpen(false)}
              />
              <div className="absolute left-0 right-0 top-full mt-1 max-h-60 overflow-y-auto bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg shadow-lg z-30 divide-y divide-stone-100 dark:divide-stone-800">
                {filteredProviders.length === 0 ? (
                  <div className="p-3 text-xs text-stone-400 text-center">
                    {providerSearch ? "No matching providers found" : "All available providers added"}
                  </div>
                ) : (
                  filteredProviders.map((p) => (
                    <button
                      key={p.slug}
                      type="button"
                      onClick={() => handleAddProvider(p.slug)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-stone-50 dark:hover:bg-stone-800/80 flex items-center justify-between group transition"
                    >
                      <div>
                        <div className="font-semibold text-stone-800 dark:text-stone-200 flex items-center gap-1.5">
                          <span>{p.name}</span>
                          {p.latency_p90_ms != null && (
                            <span className="text-[10px] font-mono text-amber-600 dark:text-amber-400">
                              ~{Math.round(p.latency_p90_ms)}ms (p90)
                            </span>
                          )}
                          {p.uptime_pct != null && (
                            <span className="text-[10px] font-mono text-sky-600 dark:text-sky-400">
                              {p.uptime_pct.toFixed(0)}% up
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-mono text-stone-400">
                          slug: {p.slug}
                          {p.description && ` — ${p.description}`}
                        </div>
                      </div>
                      <Plus className="w-3.5 h-3.5 text-stone-400 group-hover:text-stone-700 dark:group-hover:text-stone-200" />
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Latency & Throughput Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
        {/* Preferred Max Latency */}
        <div className="space-y-1">
          <label className="text-xs font-bold text-stone-800 dark:text-stone-200 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-amber-500" />
              <span>Preferred Max Latency (seconds)</span>
            </span>
            {preferences.preferred_max_latency != null && (
              <button
                type="button"
                onClick={() =>
                  onChange({
                    ...preferences,
                    preferred_max_latency: null,
                  })
                }
                className="text-[10px] text-stone-400 hover:text-stone-600 underline font-normal"
              >
                Clear
              </button>
            )}
          </label>
          <input
            type="number"
            min="0.1"
            max="120"
            step="0.1"
            value={preferences.preferred_max_latency ?? ""}
            placeholder="e.g. 2.0 (unrestricted if blank)"
            onChange={(e) => {
              const val = e.target.value.trim();
              onChange({
                ...preferences,
                preferred_max_latency: val === "" ? null : parseFloat(val),
              });
            }}
            className="w-full px-3 py-1.5 text-xs bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:ring-1 focus:ring-stone-400 font-mono shadow-2xs"
          />
          <p className="text-[10px] text-stone-500">
            OpenRouter heavily deprioritizes or falls back from endpoints exceeding this latency cutoff.
          </p>
        </div>

        {/* Preferred Min Throughput */}
        <div className="space-y-1">
          <label className="text-xs font-bold text-stone-800 dark:text-stone-200 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Gauge className="w-3.5 h-3.5 text-emerald-500" />
              <span>Preferred Min Throughput (tokens/s)</span>
            </span>
            {preferences.preferred_min_throughput != null && (
              <button
                type="button"
                onClick={() =>
                  onChange({
                    ...preferences,
                    preferred_min_throughput: null,
                  })
                }
                className="text-[10px] text-stone-400 hover:text-stone-600 underline font-normal"
              >
                Clear
              </button>
            )}
          </label>
          <input
            type="number"
            min="1"
            max="1000"
            step="5"
            value={preferences.preferred_min_throughput ?? ""}
            placeholder="e.g. 50 (unrestricted if blank)"
            onChange={(e) => {
              const val = e.target.value.trim();
              onChange({
                ...preferences,
                preferred_min_throughput: val === "" ? null : parseFloat(val),
              });
            }}
            className="w-full px-3 py-1.5 text-xs bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:ring-1 focus:ring-stone-400 font-mono shadow-2xs"
          />
          <p className="text-[10px] text-stone-500">
            Deprioritizes endpoints generating fewer tokens per second than this target rate.
          </p>
        </div>
      </div>

      {/* Advanced Provider Rules Collapsible */}
      <div className="pt-2 border-t border-stone-200/60 dark:border-stone-800/60">
        <button
          type="button"
          onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-700 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 transition"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          <span>Advanced Routing Policies & Guardrails</span>
          {isAdvancedOpen ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" />
          )}
        </button>

        {isAdvancedOpen && (
          <div className="pt-3 space-y-3">
            {/* Toggles: allow_fallbacks, require_parameters, zdr, data_collection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {/* Allow Fallbacks */}
              <label className="flex items-center justify-between p-2.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-xs cursor-pointer">
                <div>
                  <div className="font-semibold text-stone-800 dark:text-stone-200">
                    Allow Fallbacks
                  </div>
                  <div className="text-[10px] text-stone-500">
                    Fail over to backup providers if ordered ones are offline
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.allow_fallbacks !== false}
                  onChange={(e) =>
                    onChange({
                      ...preferences,
                      allow_fallbacks: e.target.checked,
                    })
                  }
                  className="rounded text-stone-900 focus:ring-stone-500 h-4 w-4"
                />
              </label>

              {/* Require Parameters */}
              <label className="flex items-center justify-between p-2.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-xs cursor-pointer">
                <div>
                  <div className="font-semibold text-stone-800 dark:text-stone-200">
                    Require Parameters
                  </div>
                  <div className="text-[10px] text-stone-500">
                    Only route to providers supporting all request parameters
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.require_parameters === true}
                  onChange={(e) =>
                    onChange({
                      ...preferences,
                      require_parameters: e.target.checked,
                    })
                  }
                  className="rounded text-stone-900 focus:ring-stone-500 h-4 w-4"
                />
              </label>

              {/* ZDR (Zero Data Retention) */}
              <label className="flex items-center justify-between p-2.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-xs cursor-pointer">
                <div>
                  <div className="font-semibold text-stone-800 dark:text-stone-200 flex items-center gap-1">
                    <Lock className="w-3 h-3 text-emerald-600" />
                    <span>Zero Data Retention (ZDR)</span>
                  </div>
                  <div className="text-[10px] text-stone-500">
                    Restrict strictly to endpoints guaranteeing zero data retention
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.zdr === true}
                  onChange={(e) =>
                    onChange({
                      ...preferences,
                      zdr: e.target.checked ? true : undefined,
                    })
                  }
                  className="rounded text-stone-900 focus:ring-stone-500 h-4 w-4"
                />
              </label>

              {/* Data Collection */}
              <label className="flex items-center justify-between p-2.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-xs cursor-pointer">
                <div>
                  <div className="font-semibold text-stone-800 dark:text-stone-200 flex items-center gap-1">
                    <Shield className="w-3 h-3 text-purple-600" />
                    <span>Deny Non-Transient Data Collection</span>
                  </div>
                  <div className="text-[10px] text-stone-500">
                    Block providers that store prompts for model training
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.data_collection === "deny"}
                  onChange={(e) =>
                    onChange({
                      ...preferences,
                      data_collection: e.target.checked ? "deny" : "allow",
                    })
                  }
                  className="rounded text-stone-900 focus:ring-stone-500 h-4 w-4"
                />
              </label>
            </div>

            {/* Provider Sorting Dropdown */}
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-stone-700 dark:text-stone-300 flex items-center gap-1">
                <ArrowUpDown className="w-3 h-3 text-stone-500" />
                <span>Sort Providers Dynamic Strategy</span>
              </label>
              <select
                value={typeof preferences.sort === "string" ? preferences.sort : ""}
                onChange={(e) =>
                  onChange({
                    ...preferences,
                    sort: e.target.value || undefined,
                  })
                }
                className="w-full px-3 py-1.5 text-xs bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:ring-1 focus:ring-stone-400 shadow-2xs"
              >
                <option value="">Default (OpenRouter Price-Weighted Balancing)</option>
                <option value="price">Price (Sort strictly by cheapest per token)</option>
                <option value="throughput">Throughput (Sort strictly by highest streaming speed)</option>
                <option value="latency">Latency (Sort strictly by lowest time-to-first-token)</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
